import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Hit, Ref } from './api'
import { ApiError } from './api'
import { Drawer } from './components/Drawer'
import { Facets } from './components/Facets'
import { Formula } from './components/Formula'
import { Header } from './components/Header'
import type { Picked } from './components/LightTable'
import { Tray } from './components/LightTable'
import { Results } from './components/Results'
import { SearchBar } from './components/SearchBar'
import { LicensePage } from './components/License'
import { AccountPage, CartPage, CheckoutPage, OrderPage } from './components/ShopPages'
import { match, navigate, useLocation } from './router'
import { syncUrl, useDebounced, useMe, useResults, useSearchState, useStored, useTheme } from './state'
import type { Group } from './tree'
import { toggleFacet } from './tree'

export default function App() {
  const [theme, setTheme] = useTheme()
  const { data: me } = useMe()
  const { path, search } = useLocation()

  let page: React.ReactNode
  let m: Record<string, string> | null
  if ((m = match('/lizenz/:id', path))) {
    const item = new URLSearchParams(search).get('item')
    page = <LicensePage key={`${m.id}-${item}`} id={Number(m.id)} itemId={item ? Number(item) : null} />
  } else if (path === '/warenkorb') page = <CartPage />
  else if (path === '/kasse') page = <CheckoutPage />
  else if (path === '/konto' || path === '/bestellungen') page = <AccountPage />
  else if ((m = match('/bestellungen/:number', path))) page = <OrderPage key={m.number} number={m.number} search={search} />
  else page = <SearchPage />

  return (
    <>
      <Header me={me} theme={theme} setTheme={setTheme} total={me?.visible_total ?? undefined} path={path} />
      {page}
      <footer className="foot wrap">
        Bilddaten: easydb der Stiftung Günter Zint. Alle Bilder sind urheberrechtlich geschützt; Nutzung nur mit Lizenz.
      </footer>
    </>
  )
}

function SearchPage() {
  const { data: me } = useMe()
  const { tree, setTree, sort, setSort, view, setView, ready, initError } = useSearchState()
  const searchTree = useDebounced(tree, 300)
  const results = useResults(searchTree, sort, ready)
  const [openId, setOpenId] = useState<number | null>(null)
  const [facetsOpen, setFacetsOpen] = useState(false)
  const [formulaError, setFormulaError] = useState<string | null>(initError)
  const [picked, setPicked] = useStored<Picked[]>('zint-lighttable', [])
  const [saved, setSaved] = useStored<string[]>('zint-saved', [])

  const pages = results.data?.pages ?? []
  const first = pages[0]
  const hits = useMemo(() => pages.flatMap((p) => p.hits), [pages])
  const text = first?.text ?? ''

  useEffect(() => { if (initError) setFormulaError(initError) }, [initError])
  useEffect(() => { if (first && ready) syncUrl(first.text, sort, view) }, [first, sort, view, ready])

  const searchError = results.error
    ? results.error instanceof ApiError
      ? results.error.kind === 'syntax' || results.error.kind === 'query' ? results.error.message : `${results.error.message}`
      : 'Die Suche ist fehlgeschlagen.'
    : null

  const onFilter = useCallback((field: string, ref: Ref) => {
    if (ref.id == null) return
    setTree((t) => toggleFacet(t, field, ref.id!, ref.label))
    setOpenId(null)
  }, [setTree])

  const onPick = useCallback((h: Hit) => {
    setPicked((list) => list.some((p) => p.id === h.id) ? list.filter((p) => p.id !== h.id)
      : [...list, { id: h.id, inv: h.inv, title: h.title, date: h.date, images: h.images }])
  }, [setPicked])
  const pickedIds = useMemo(() => new Set(picked.map((p) => p.id)), [picked])

  const index = openId == null ? -1 : hits.findIndex((h) => h.id === openId)
  const onNav = useCallback((dir: -1 | 1) => {
    const i = hits.findIndex((h) => h.id === openId) + dir
    if (hits[i]) setOpenId(hits[i].id)
    if (dir === 1 && i >= hits.length - 3 && results.hasNextPage && !results.isFetchingNextPage) results.fetchNextPage()
  }, [hits, openId, results])

  return (
    <>
      <div className="band wrap">
        <SearchBar
          onAdd={(nodes) => setTree((t: Group) => ({ ...t, items: [...t.items, ...nodes] }))}
          onReplace={(t) => setTree(t)}
          onError={setFormulaError} />
        <Formula tree={tree} setTree={setTree} text={text} canUseRaw={!!me?.can_use_raw}
          error={formulaError} setError={setFormulaError}
          onSave={() => text && setSaved((s) => [text, ...s.filter((x) => x !== text)].slice(0, 10))} />
        {saved.length > 0 && (
          <div className="examples">
            <span className="label">Gemerkt</span>
            {saved.map((s) => (
              <span key={s} className="chip" style={{ borderColor: 'var(--rule)' }}>
                <button title={s} onClick={() => { window.location.search = `?q=${encodeURIComponent(s)}` }}>{s.length > 46 ? s.slice(0, 44) + '…' : s}</button>
                <button className="x" aria-label="Gemerkte Suche löschen" onClick={() => setSaved((l) => l.filter((x) => x !== s))}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="main wrap">
        <Facets facets={first?.facets} tree={tree} setTree={setTree} open={facetsOpen} isResearcher={!!me?.is_researcher} />
        <Results
          hits={hits} total={first?.total} took={first?.took_ms}
          loading={results.isFetching && !results.isFetchingNextPage}
          error={searchError} warnings={first?.warnings ?? []}
          tree={tree} setTree={setTree} sort={sort} setSort={setSort} view={view} setView={setView}
          hasMore={!!results.hasNextPage} loadMore={() => results.fetchNextPage()} loadingMore={results.isFetchingNextPage}
          onOpen={setOpenId} picked={pickedIds} onPick={onPick}
          toggleFacets={() => setFacetsOpen((o) => !o)} isPublic={!me?.authenticated} />
      </div>
      {openId != null && (
        <Drawer id={openId} index={index} count={first?.total ?? hits.length}
          onClose={() => setOpenId(null)} onNav={onNav} onOpen={setOpenId} onFilter={onFilter}
          picked={pickedIds.has(openId)} onPick={onPick} onLicense={(id) => navigate(`/lizenz/${id}`)} />
      )}
      <Tray items={picked} onClear={() => setPicked([])} onRemove={(id) => setPicked((l) => l.filter((p) => p.id !== id))}
        onReorder={setPicked} onDetails={setOpenId} />
    </>
  )
}
